import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const BarChartComponent = ({ data, dataKeyX = "name", dataKeyY = "value" }) => {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "var(--font-main)",
          fontSize: "0.85rem",
          color: "var(--color-text-light)",
        }}
      >
        No data available
      </div>
    );
  }

  const isMultiSeries = Array.isArray(dataKeyY);

  const max = isMultiSeries
    ? Math.max(...data.flatMap((item) => dataKeyY.map((key) => item[key] || 0)))
    : Math.max(...data.map((item) => item[dataKeyY] || 0));

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <ResponsiveContainer>
        <BarChart
          data={data}
          margin={{ top: 18, right: 8, left: -34, bottom: -8 }}
        >
          <XAxis
            dataKey={dataKeyX}
            tickLine={false}
            axisLine={{ stroke: "var(--color-border)" }}
            tick={{
              fontSize: 12,
              fontWeight: 500,
              fontFamily: "var(--font-main)",
              fill: "var(--color-text-light)",
            }}
          />
          <YAxis
            tickLine={false}
            axisLine={{ stroke: "var(--color-border)" }}
            tick={{
              fontSize: 12,
              fontWeight: 500,
              fontFamily: "var(--font-main)",
              fill: "var(--color-text-light)",
            }}
          />

          <Tooltip
            cursor={{ fill: "rgba(148, 163, 184, 0.08)" }}
            content={<CompactTooltip />}
          />

          {isMultiSeries ? (
            dataKeyY.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                radius={[6, 6, 0, 0]}
                fill={
                  index % 2 === 0
                    ? "var(--color-primary)"
                    : "var(--color-primary-light)"
                }
                barSize={40}
              />
            ))
          ) : (
            <Bar dataKey={dataKeyY} radius={[6, 6, 0, 0]} barSize={28}>
              {data.map((entry, idx) => {
                const value = entry[dataKeyY] || 0;
                const hue = 221; // navy-ish
                const saturation = 65;
                const lightness = 78 - (value / max) * 35;
                const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
                return <Cell key={`cell-${idx}`} fill={color} />;
              })}
            </Bar>
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const CompactTooltip = ({ active, label, payload }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div
      style={{
        backgroundColor: "var(--color-bg-light)",
        borderRadius: "0.4rem",
        border: `1px solid var(--color-border)`,
        padding: "0.2rem 0.45rem",
        fontFamily: "var(--font-main)",
        fontSize: "0.75rem",
        lineHeight: 1.2,
      }}
    >
      <div style={{ margin: 0 }}>{label}</div>
      {payload.map((item) => {
        const value =
          typeof item.value === "number" ? item.value.toFixed(2) : item.value;

        return (
          <div key={item.dataKey} style={{ margin: 0 }}>
            {item.name ?? item.dataKey}: {value}
          </div>
        );
      })}
    </div>
  );
};

export default BarChartComponent;
